/**
 * Rate limits — throttling policies for ingress sources and egress targets.
 *
 * Two sections in one page (Ingress, Egress). Each section pins the
 * seed default row at the top with a "Default" badge — defaults are
 * editable (admin can dial the global cap) but never deletable, so the
 * runtime always has a fallback.
 *
 * Specific (non-default) rows override the default for one source or
 * one target. Auto-discovery feeds the egress autocomplete in the
 * create dialog: admins pick from the hosts the system has actually
 * called rather than typing them from memory.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge, Pencil, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import {
  RateLimitDialog,
  type RateLimitDialogMode,
} from "@/components/rate-limits/RateLimitDialog";
import { TagList } from "@/components/tags";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { ingressApi } from "@/lib/api-ingress";
import {
  rateLimitsApi,
  type RateLimit,
  type RateLimitType,
} from "@/lib/api-rate-limits";
import { usePermissions } from "@/lib/permissions";


/** Render compact limits summary: "50/s · 8 conc" or "unlimited". */
function formatLimits(r: RateLimit): string {
  const parts: string[] = [];
  if (r.max_qps != null) parts.push(`${r.max_qps}/s`);
  if (r.max_concurrency != null) parts.push(`${r.max_concurrency} conc`);
  return parts.length === 0 ? "unlimited" : parts.join(" · ");
}


export function RateLimitsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("rate_limits", "write");
  const canDelete = can("rate_limits", "delete");

  const [dialog, setDialog] = React.useState<{
    open: boolean;
    mode: RateLimitDialogMode;
  }>({ open: false, mode: { kind: "create" } });

  const listQ = useQuery({
    queryKey: ["rate-limits"],
    queryFn: () => rateLimitsApi.list(),
  });

  // Pull the ingress sources once so we can show their names instead of
  // raw UUIDs in the ingress section.
  const ingressQ = useQuery({
    queryKey: ["rate-limits", "ingress-name-lookup"],
    queryFn: () => ingressApi.list({ page_size: 100 }),
    staleTime: 60_000,
  });
  const ingressNames = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const src of ingressQ.data?.items ?? []) m.set(src.id, src.name);
    return m;
  }, [ingressQ.data]);

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      rateLimitsApi.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rate-limits"] }),
  });

  const remove = useMutation({
    mutationFn: rateLimitsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rate-limits"] }),
  });

  const rows = listQ.data ?? [];
  const ingressRows = rows.filter((r) => r.type === "ingress");
  const egressRows = rows.filter((r) => r.type === "egress");

  return (
    <PageContainer>
      <PageHeader
        title={t("rateLimits.title", "Rate limits")}
        description={t(
          "rateLimits.subtitle",
          "Throttle how much we receive on ingress sources and send to egress targets. Edit the defaults to set a global cap, or add a specific rule per source/target.",
        )}
        actions={
          canWrite && (
            <Button
              size="sm"
              onClick={() => setDialog({ open: true, mode: { kind: "create" } })}
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("rateLimits.new", "New rate limit")}
            </Button>
          )
        }
      />
      <PageContent>
        {rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Gauge}
            title={t("rateLimits.empty", "No rate limits configured")}
            description={t(
              "rateLimits.emptyDescription",
              "The default ingress and egress policies will appear here once the database is fully migrated.",
            )}
          />
        ) : (
          <div className="space-y-6">
            <Section
              type="ingress"
              title="Ingress — what we receive"
              rows={ingressRows}
              ingressNames={ingressNames}
              canWrite={canWrite}
              canDelete={canDelete}
              onToggle={(id, enabled) => toggle.mutate({ id, enabled })}
              onEdit={(r) =>
                setDialog({ open: true, mode: { kind: "edit", rateLimit: r } })
              }
              onDelete={(r) => {
                if (confirm(`Delete rate limit for "${labelFor(r, ingressNames)}"?`)) {
                  remove.mutate(r.id);
                }
              }}
            />
            <Section
              type="egress"
              title="Egress — what we send"
              rows={egressRows}
              ingressNames={ingressNames}
              canWrite={canWrite}
              canDelete={canDelete}
              onToggle={(id, enabled) => toggle.mutate({ id, enabled })}
              onEdit={(r) =>
                setDialog({ open: true, mode: { kind: "edit", rateLimit: r } })
              }
              onDelete={(r) => {
                if (confirm(`Delete rate limit for "${labelFor(r, ingressNames)}"?`)) {
                  remove.mutate(r.id);
                }
              }}
            />
          </div>
        )}
      </PageContent>

      <RateLimitDialog
        open={dialog.open}
        mode={dialog.mode}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
      />
    </PageContainer>
  );
}


function labelFor(r: RateLimit, ingressNames: Map<string, string>): string {
  if (r.is_default) return `Default ${r.type}`;
  if (r.type === "ingress") {
    return ingressNames.get(r.ingress_source_id ?? "") ?? r.ingress_source_id ?? "?";
  }
  return `${r.egress_target_kind}:${r.egress_target_value}`;
}


function Section({
  type,
  title,
  rows,
  ingressNames,
  canWrite,
  canDelete,
  onToggle,
  onEdit,
  onDelete,
}: {
  type: RateLimitType;
  title: string;
  rows: RateLimit[];
  ingressNames: Map<string, string>;
  canWrite: boolean;
  canDelete: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onEdit: (r: RateLimit) => void;
  onDelete: (r: RateLimit) => void;
}) {
  // Defaults pinned at the top, the rest in creation order.
  const sorted = React.useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
        return a.created_at.localeCompare(b.created_at);
      }),
    [rows],
  );

  return (
    <section>
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      <div className="overflow-hidden rounded-lg border border-border/60">
        <Table>
          <Thead>
            <Tr>
              <Th className="w-12">On</Th>
              <Th>{type === "ingress" ? "Source" : "Destination"}</Th>
              <Th className="w-44">Limits</Th>
              <Th className="w-32">On breach</Th>
              <Th>Description</Th>
              <Th>Tags</Th>
              {(canWrite || canDelete) && (
                <Th className="w-24 text-right">Actions</Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {sorted.map((r) => (
              <Tr key={r.id}>
                <Td onClick={(e) => e.stopPropagation()}>
                  {canWrite ? (
                    <Switch
                      checked={r.enabled}
                      onCheckedChange={(v) => onToggle(r.id, v)}
                    />
                  ) : (
                    <Badge tone={r.enabled ? "active" : "neutral"}>
                      {r.enabled ? "on" : "off"}
                    </Badge>
                  )}
                </Td>
                <Td>
                  <div className="flex items-center gap-2 font-medium">
                    {r.is_default ? (
                      <span className="text-muted-foreground">
                        Any {r.type}
                      </span>
                    ) : (
                      <span>{labelFor(r, ingressNames)}</span>
                    )}
                    {r.is_default && (
                      <Badge tone="neutral" className="text-[10px]">
                        default
                      </Badge>
                    )}
                  </div>
                  {!r.is_default && r.type === "egress" && (
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {r.egress_target_kind}
                    </div>
                  )}
                </Td>
                <Td className="font-mono text-xs">
                  {formatLimits(r)}
                  {r.burst != null && (
                    <span className="ml-1 text-muted-foreground">
                      (burst {r.burst})
                    </span>
                  )}
                </Td>
                <Td className="text-xs">{r.on_breach}</Td>
                <Td className="text-xs text-muted-foreground">
                  {r.description ?? ""}
                </Td>
                <Td>
                  <TagList tags={r.tags} max={3} />
                </Td>
                {(canWrite || canDelete) && (
                  <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit"
                          onClick={() => onEdit(r)}
                        >
                          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                      )}
                      {canDelete && !r.is_default && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete"
                          onClick={() => onDelete(r)}
                        >
                          <Trash2
                            className="h-3.5 w-3.5 text-danger"
                            strokeWidth={1.5}
                          />
                        </Button>
                      )}
                    </div>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    </section>
  );
}
